import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, request } from '@/lib/api/client'
import { asList } from '@/lib/api/list'

/**
 * Müşteri giderleri: personel müşteri adına yaptığı harcamayı ödeme şekli, açıklama ve fiş/dekontla girer; firma sahibi
 * onaylar, reddeder ya da açıklama ister. Onaylanan nakit/şahsi kart gideri personele geri ödenir (Finans → Geri ödemeler);
 * şirket kartı gideri kartın hareketi olur; iş avansından ödenen gider avanstan düşülür (aşan kısım geri ödenir). Faturalı
 * giderde KDV 191 İndirilecek KDV'ye ayrılır (hediyede gidere dahil kalır).
 */
export const EXPENSE_PERMS = {
  create: 'crm:expense:create',
  editOwn: 'crm:expense:edit_own',
  readAll: 'crm:expense:read_all',
  approve: 'crm:expense:approve',
  postLegacy: 'crm:expense:post_legacy',
} as const

export const EXPENSE_CATEGORIES = ['yemek', 'ulasim', 'konaklama', 'hediye', 'temsil', 'diger'] as const
export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number]
export const PAYMENT_METHODS = ['cash', 'personal_card', 'company_card', 'advance'] as const
export type PaymentMethod = (typeof PAYMENT_METHODS)[number]
export type DocumentType = 'receipt' | 'invoice'
export const VAT_RATES = ['0', '1', '10', '20'] as const
/** Müşteriye verilen hediyenin KDV'si indirilmez (gidere dahil kalır). */
export const vatDeductible = (category: string) => category !== 'hediye'
/** KDV dahil tutarın içindeki KDV (kuruşa yuvarlı). */
export const vatOf = (gross: number, rate: number) => (rate > 0 && gross > 0 ? Math.round(((gross * rate) / (100 + rate)) * 100) / 100 : 0)
export type ExpenseStatus = 'draft' | 'pending' | 'info_requested' | 'approved' | 'rejected' | 'cancelled'
export const EXPENSE_STATUS_TONE: Record<ExpenseStatus, 'neutral' | 'warning' | 'info' | 'success' | 'danger'> = {
  draft: 'neutral',
  pending: 'warning',
  info_requested: 'info',
  approved: 'success',
  rejected: 'danger',
  cancelled: 'neutral',
}

export type ExpenseFile = { id: string; name: string; mime: string; size: number; createdAt: string }
export type ExpenseEvent = { userId: string; kind: string; note: string; createdAt: string }

export type CustomerExpense = {
  id: string
  accountId: string
  meetingId: string
  projectId: string
  userId: string
  title: string
  description: string
  category: ExpenseCategory
  amount: string
  spentOn: string
  paymentMethod: PaymentMethod
  cardId: string
  documentType: DocumentType
  vatRate: string
  vatAmount: string
  invoiceNo: string
  vatDeductible: boolean
  status: ExpenseStatus
  submittedAt: string
  decidedAt: string
  decidedBy: string
  decisionNote: string
  financeRef: string
  legacy: boolean
  fileCount: number
  createdAt: string
  files: ExpenseFile[]
  events: ExpenseEvent[]
  canEdit: boolean
  canDecide: boolean
  canPostLegacy: boolean
}

type Row = Record<string, unknown>
const s = (v: unknown) => (v == null ? '' : String(v))

const mapExpense = (r: Row): CustomerExpense => ({
  id: s(r.id),
  accountId: s(r.account_id),
  meetingId: s(r.meeting_id),
  projectId: s(r.project_id),
  userId: s(r.user_id),
  title: s(r.title),
  description: s(r.description),
  category: (EXPENSE_CATEGORIES as readonly string[]).includes(s(r.category)) ? (s(r.category) as ExpenseCategory) : 'diger',
  amount: s(r.amount),
  spentOn: s(r.spent_on),
  paymentMethod: (PAYMENT_METHODS as readonly string[]).includes(s(r.payment_method)) ? (s(r.payment_method) as PaymentMethod) : 'cash',
  cardId: s(r.card_id),
  documentType: r.document_type === 'invoice' ? 'invoice' : 'receipt',
  vatRate: s(r.vat_rate) || '0',
  vatAmount: s(r.vat_amount) || '0.00',
  invoiceNo: s(r.invoice_no),
  vatDeductible: r.vat_deductible === true,
  status: s(r.status) as ExpenseStatus,
  submittedAt: s(r.submitted_at),
  decidedAt: s(r.decided_at),
  decidedBy: s(r.decided_by),
  decisionNote: s(r.decision_note),
  financeRef: s(r.finance_ref),
  legacy: r.legacy === true,
  fileCount: Number(r.file_count) || 0,
  createdAt: s(r.created_at),
  files: asList(r.files as Row[]).map((f) => ({ id: s(f.id), name: s(f.name), mime: s(f.mime), size: Number(f.size) || 0, createdAt: s(f.created_at) })),
  events: asList(r.events as Row[]).map((e) => ({ userId: s(e.user_id), kind: s(e.kind), note: s(e.note), createdAt: s(e.created_at) })),
  canEdit: r.can_edit === true,
  canDecide: r.can_decide === true,
  canPostLegacy: r.can_post_legacy === true,
})

export const expenseKeys = {
  all: ['crm', 'customer-expenses'] as const,
  list: (f: Record<string, string>) => ['crm', 'customer-expenses', 'list', f] as const,
  one: (id: string) => ['crm', 'customer-expenses', 'one', id] as const,
}

export const useCustomerExpenses = (f: { accountId?: string; meetingId?: string; status?: string; userId?: string }, enabled = true) => {
  const q = { account_id: f.accountId ?? '', meeting_id: f.meetingId ?? '', status: f.status ?? '', user_id: f.userId ?? '' }
  return useQuery({
    queryKey: expenseKeys.list(q),
    enabled,
    queryFn: async () => asList(await api.get<Row[]>('/api/v1/customer-expenses', q)).map(mapExpense),
  })
}

export const useCustomerExpense = (id: string | null) =>
  useQuery({ queryKey: expenseKeys.one(id ?? ''), enabled: !!id, queryFn: async () => mapExpense(await api.get<Row>(`/api/v1/customer-expenses/${id}`)) })

export type CardOption = { id: string; name: string; last4: string; bankName: string }

export type PaymentOptions = { cards: CardOption[]; advanceBalance: string }

/** Zimmetli şirket kartları ve açık iş avansı bakiyesi (gider formunda). */
export const usePaymentOptions = (enabled = true) =>
  useQuery({
    queryKey: ['crm', 'customer-expenses', 'payment-options'],
    enabled,
    queryFn: async (): Promise<PaymentOptions> => {
      const r = await api.get<Row>('/api/v1/customer-expenses/payment-options')
      return {
        cards: asList(r.cards as Row[]).map((c): CardOption => ({ id: s(c.id), name: s(c.name), last4: s(c.last4), bankName: s(c.bank_name) })),
        advanceBalance: s(r.advance_balance) || '0.00',
      }
    },
  })

export type ExpenseInput = {
  accountId: string
  meetingId: string
  title: string
  description: string
  category: ExpenseCategory
  amount: string
  spentOn: string
  paymentMethod: PaymentMethod
  cardId: string
  documentType: DocumentType
  vatRate: string
  invoiceNo: string
}

const docBody = (i: { documentType: DocumentType; vatRate: string; invoiceNo: string }) => ({
  document_type: i.documentType,
  vat_rate: i.documentType === 'invoice' ? i.vatRate : '0',
  invoice_no: i.documentType === 'invoice' ? i.invoiceNo.trim() : '',
})

const body = (i: ExpenseInput) => ({
  account_id: i.accountId,
  meeting_id: i.meetingId,
  title: i.title,
  description: i.description,
  category: i.category,
  amount: i.amount.replace(',', '.'),
  spent_on: i.spentOn,
  payment_method: i.paymentMethod,
  card_id: i.paymentMethod === 'company_card' ? i.cardId : '',
  ...docBody(i),
})

function useExpMut<A, R = unknown>(fn: (a: A) => Promise<R>) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: expenseKeys.all }) })
}

export const useSaveExpense = () =>
  useExpMut(async ({ id, input }: { id?: string; input: ExpenseInput }) =>
    id ? (await api.put(`/api/v1/customer-expenses/${id}`, body(input)), id) : s((await api.post<Row>('/api/v1/customer-expenses', body(input))).id),
  )

/** Belge yükleme (tek dosya; çağıran sırayla gönderir ve ilerlemeyi gösterir). */
export const uploadExpenseFile = (id: string, file: File) => {
  const form = new FormData()
  form.append('file', file)
  return request('POST', `/api/v1/customer-expenses/${id}/files`, { body: form })
}

export const useDeleteExpenseFile = () => useExpMut(({ id, fileId }: { id: string; fileId: string }) => api.del(`/api/v1/customer-expenses/${id}/files/${fileId}`))

export const downloadExpenseFile = (id: string, f: ExpenseFile) => api.download(`/api/v1/customer-expenses/${id}/files/${f.id}`, f.name)

export const useExpenseAction = () =>
  useExpMut(({ id, action, note, decision }: { id: string; action: 'submit' | 'respond' | 'cancel' | 'decide'; note?: string; decision?: 'approve' | 'reject' | 'request_info' }) =>
    api.post(`/api/v1/customer-expenses/${id}/${action}`, { action: decision ?? '', note: note ?? '' }),
  )

export type LegacyPostInput = { exclude: boolean; note: string; paymentMethod: PaymentMethod; cardId: string; documentType: DocumentType; vatRate: string; invoiceNo: string }

/** Eski (önceki kayıt) gideri muhasebeye aktarır ya da muhasebe dışı bırakır. */
export const usePostLegacyExpense = () =>
  useExpMut(({ id, input: i }: { id: string; input: LegacyPostInput }) =>
    api.post(`/api/v1/customer-expenses/${id}/post-legacy`, {
      exclude: i.exclude,
      note: i.note.trim(),
      payment_method: i.paymentMethod,
      card_id: i.paymentMethod === 'company_card' ? i.cardId : '',
      ...docBody(i),
    }),
  )

// --- Finans: personel geri ödemeleri ---

export const REIMB_PERMS = { read: 'finance:reimbursement:read', pay: 'finance:reimbursement:pay' } as const

export type Reimbursement = { id: string; sourceId: string; userId: string; userName: string; amount: string; description: string; status: 'open' | 'paid'; createdAt: string; paidAt: string }

export const useReimbursements = (status: string) =>
  useQuery({
    queryKey: ['finance', 'reimbursements', status],
    queryFn: async () =>
      asList(await api.get<Row[]>('/api/v1/finance/reimbursements', { status })).map(
        (r): Reimbursement => ({
          id: s(r.id),
          sourceId: s(r.source_id),
          userId: s(r.user_id),
          userName: s(r.user_name),
          amount: s(r.amount),
          description: s(r.description),
          status: r.status === 'paid' ? 'paid' : 'open',
          createdAt: s(r.created_at),
          paidAt: s(r.paid_at),
        }),
      ),
  })

export const usePayReimbursement = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, accountId, date }: { id: string; accountId: string; date: string }) =>
      api.post(`/api/v1/finance/reimbursements/${id}/pay`, { account_id: accountId, date }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['finance'] }),
  })
}

// --- Finans: personel iş avansları ---

export const STAFF_ADVANCE_PERMS = { read: 'finance:staff_advance:read', give: 'finance:staff_advance:give', ret: 'finance:staff_advance:return' } as const

export type AdvanceBalance = { userId: string; userName: string; given: string; spent: string; returned: string; balance: string; lastMoveAt: string }
export type AdvanceMove = { id: string; userId: string; userName: string; kind: 'give' | 'expense' | 'return'; amount: string; date: string; description: string; sourceId: string }

export const useAdvanceBalances = () =>
  useQuery({
    queryKey: ['finance', 'staff-advances'],
    queryFn: async () =>
      asList(await api.get<Row[]>('/api/v1/finance/staff-advances')).map(
        (r): AdvanceBalance => ({
          userId: s(r.user_id),
          userName: s(r.user_name),
          given: s(r.given),
          spent: s(r.spent),
          returned: s(r.returned),
          balance: s(r.balance),
          lastMoveAt: s(r.last_move_at),
        }),
      ),
  })

export const useAdvanceMoves = (userId: string, enabled = true) =>
  useQuery({
    queryKey: ['finance', 'staff-advances', 'moves', userId],
    enabled,
    queryFn: async () =>
      asList(await api.get<Row[]>('/api/v1/finance/staff-advances/moves', { user_id: userId })).map(
        (r): AdvanceMove => ({
          id: s(r.id),
          userId: s(r.user_id),
          userName: s(r.user_name),
          kind: (['give', 'expense', 'return'].includes(s(r.kind)) ? s(r.kind) : 'give') as AdvanceMove['kind'],
          amount: s(r.amount),
          date: s(r.date),
          description: s(r.description),
          sourceId: s(r.source_id),
        }),
      ),
  })

export type AdvanceInput = { userId: string; accountId: string; amount: string; date: string; description: string }

export const useAdvanceAction = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ kind, input: i }: { kind: 'give' | 'return'; input: AdvanceInput }) =>
      api.post(`/api/v1/finance/staff-advances/${kind}`, { user_id: i.userId, account_id: i.accountId, amount: i.amount.replace(',', '.'), date: i.date, description: i.description.trim() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['finance'] }),
  })
}

export const useAdvanceStaff = (enabled = true) =>
  useQuery({
    queryKey: ['finance', 'staff-advances', 'staff'],
    enabled,
    staleTime: 60_000,
    queryFn: async () => asList(await api.get<Row[]>('/api/v1/finance/staff-advances/staff')).map((r) => ({ id: s(r.id), name: s(r.name) })),
  })
